import { FsnOrderStatusNotice } from './FsnOrderStatusNotice'
import { Badge } from '@/components/ui/badge'
import { Typography } from '@/components/ui/typography'
import { salesOrderFixAddressSearch } from '@/lib/services/sales-orders/sales-order.schema'
import { cn } from '@/lib/utils'
import { Lock } from 'lucide-react'
import { DeliveryAddressVerificationBadge } from './DeliveryAddressVerificationBadge'
import { DeliveryAttemptTimeline } from './DeliveryAttemptTimeline'
import DeliveryRouteOrderItemsDisclosure from './DeliveryRouteOrderItemsDisclosure'
import DeliveryRouteOrderLink from './DeliveryRouteOrderLink'
import { isDeliveryAddressExplicitlyUnverified } from './delivery-address-verification'
import {
  formatDeliveryRouteDate,
  formatDeliveryRouteDateTime,
  getDeliveryRouteOrderAddress,
  getDeliveryRouteOrderLabel,
  isDeliveryRouteOrderOutForDelivery,
} from './delivery-share.utils'

export default function DeliveryRouteStopCard({ isEditing = false, order, stopNumber }) {
  const orderLabel = getDeliveryRouteOrderLabel(order)
  const primaryLabel = order.recipient_name || orderLabel
  const isManualDelivery = order.source_type === 'manual'
  const isAddressUnverified = isDeliveryAddressExplicitlyUnverified(order.delivery_address)
  const isOutForDelivery = isDeliveryRouteOrderOutForDelivery(order)
  const statusTone = order.is_delivered
    ? 'success'
    : order.is_route_missed
      ? 'warning'
      : isOutForDelivery
        ? 'info'
        : 'neutral'
  const stopNumberTone = order.is_delivered
    ? 'border-success/20 bg-success text-success-foreground'
    : order.is_route_missed
      ? 'border-warning/25 bg-warning text-warning-foreground'
      : isOutForDelivery
        ? 'border-info/25 bg-info text-info-foreground'
        : 'border-action/20 bg-action text-action-foreground'

  return (
    <div
      data-tone={statusTone}
      className={cn(
        '@container rounded-lg border p-4',
        order.is_delivered
          ? 'border-status-success-border bg-status-success-background'
          : order.is_route_missed
            ? 'border-status-warning-border bg-status-warning-background'
            : isOutForDelivery
              ? 'border-status-info-border bg-status-info-background'
              : 'bg-card'
      )}
    >
      <div className="space-y-4">
        <FsnOrderStatusNotice change={order.fsn_change} />
        <div className="flex flex-col gap-4 @lg:flex-row @lg:items-start @lg:justify-between">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <span
              className={cn(
                'mt-0.5 flex size-11 shrink-0 items-center justify-center rounded-full border text-lg font-semibold shadow-sm',
                stopNumberTone
              )}
            >
              {stopNumber}
            </span>

            <div className="min-w-0 flex-1 space-y-3">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <Typography variant="h5" className="break-words">
                    {order.recipient_name || isManualDelivery ? (
                      primaryLabel
                    ) : (
                      <DeliveryRouteOrderLink
                        order={order}
                        className="text-foreground hover:text-interaction"
                      >
                        {orderLabel}
                      </DeliveryRouteOrderLink>
                    )}
                  </Typography>
                  {order.is_delivered ? (
                    <Badge variant="success">Delivered</Badge>
                  ) : order.is_route_missed ? (
                    <Badge variant="warning">Missed</Badge>
                  ) : isOutForDelivery ? (
                    <Badge variant="info">Out for delivery</Badge>
                  ) : (
                    <Badge variant="outline">Pending</Badge>
                  )}
                  {order.delivery_attempt_count > 0 ? (
                    <Badge variant="brand">
                      {order.delivery_attempt_count} attempt
                      {order.delivery_attempt_count === 1 ? '' : 's'}
                    </Badge>
                  ) : null}
                  {isManualDelivery ? <Badge variant="secondary">Custom stop</Badge> : null}
                  {isEditing && order.is_delivered ? (
                    <Badge variant="warning">
                      <Lock className="size-3" />
                      Locked
                    </Badge>
                  ) : null}
                </div>

                {order.recipient_name && !isManualDelivery ? (
                  <Typography variant="label" tone="muted" className="block">
                    <DeliveryRouteOrderLink
                      order={order}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      {orderLabel}
                    </DeliveryRouteOrderLink>
                  </Typography>
                ) : null}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <DeliveryAddressVerificationBadge
                  deliveryAddress={order.delivery_address}
                  tooltipSide="bottom"
                />
                <Typography variant="supporting">{getDeliveryRouteOrderAddress(order)}</Typography>
              </div>

              {isAddressUnverified ? (
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-status-destructive-border bg-status-destructive-background px-3 py-2">
                  <Badge variant="destructive">Address needs verification</Badge>
                  <Typography variant="label" tone="muted">
                    Verify this address before rerouting this stop.
                  </Typography>
                  {!isManualDelivery ? (
                    <DeliveryRouteOrderLink
                      order={order}
                      searchParams={salesOrderFixAddressSearch}
                      className="text-sm font-medium text-destructive hover:text-destructive"
                    >
                      Fix address
                    </DeliveryRouteOrderLink>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          <div className="space-y-1 border-t border-border/60 pt-3 text-left @lg:shrink-0 @lg:border-t-0 @lg:pt-0 @lg:text-right">
            <Typography variant="label" tone="muted" className="block">
              Due {formatDeliveryRouteDate(order.fulfillment_due_date)}
            </Typography>
            {order.is_delivered && order.fulfilled_at ? (
              <Typography variant="supporting" className="block">
                Delivered {formatDeliveryRouteDateTime(order.fulfilled_at)}
              </Typography>
            ) : null}
          </div>
        </div>

        {order.delivery_attempt_count > 0 ? (
          <DeliveryAttemptTimeline
            attempts={order.delivery_attempts || []}
            emptyMessage="No delivery attempts logged yet."
          />
        ) : null}

        {!isManualDelivery ? (
          <DeliveryRouteOrderItemsDisclosure
            order={order}
            panelId={`route-stop-${order.selection_id ?? order.id}-items`}
            className="border-t border-border/60 pt-3"
          />
        ) : null}
      </div>
    </div>
  )
}
